// ============================================================================
// KLINISOL CRO - Training Records Page
// Purpose: User's personal training history, certificates, and audit trail
// FDA 21 CFR Part 11 Compliant - Complete training transcript
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import SessionManager from '../components/SessionManager';
import { 
    Award,
    Download,
    FileText,
    Calendar,
    CheckCircle,
    TrendingUp,
    Filter,
    Search,
    ArrowLeft,
    Printer,
    Clock,
    Loader2,
    AlertCircle,
    ExternalLink
} from 'lucide-react';

const TrainingRecords = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    // State management
    const [records, setRecords] = useState([]);
    const [filteredRecords, setFilteredRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedYear, setSelectedYear] = useState('all');

    // Fetch training records on mount
    useEffect(() => {
        fetchTrainingRecords();
    }, []);

    // Apply filters
    useEffect(() => {
        applyFilters();
    }, [searchTerm, selectedCategory, selectedYear, records]);

    const fetchTrainingRecords = async () => {
        try {
            setLoading(true);
            setError('');

            const response = await api.get('/training/records/my');
            
            if (response.data.success) {
                // Only show completed or certified records
                const completedRecords = response.data.data.filter(
                    record => record.status === 'completed' || record.status === 'certified'
                );
                setRecords(completedRecords);
                setFilteredRecords(completedRecords);
            } else {
                setError('Failed to load training records');
            }
        } catch (err) {
            console.error('Error fetching training records:', err);
            setError(err.response?.data?.message || 'Failed to load training records. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...records];

        // Filter by category
        if (selectedCategory !== 'all') {
            filtered = filtered.filter(record => record.category === selectedCategory);
        }

        // Filter by year
        if (selectedYear !== 'all') {
            filtered = filtered.filter(record => {
                if (!record.completion_date) return false; // Skip NULL dates
                const recordYear = new Date(record.completion_date).getFullYear().toString();
                return recordYear === selectedYear;
            });
        }

        // Filter by search term
        if (searchTerm.trim()) {
            const search = searchTerm.toLowerCase();
            filtered = filtered.filter(record =>
                record.module_name.toLowerCase().includes(search) ||
                record.module_code.toLowerCase().includes(search)
            );
        }

        setFilteredRecords(filtered);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Invalid Date';
            
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                timeZone: 'UTC'
            });
        } catch (error) {
            console.error('Error formatting date:', error);
            return 'Invalid Date';
        }
    };

    const handlePrintTranscript = () => {
        window.print();
    };

    const handleDownloadCertificate = async (record) => {
        try {
            // Use FDA-compliant backend endpoint with proper authentication
            const response = await api.get(`/training/certificate/${record.id}`, {
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Certificate_${record.module_code}_${record.module_name.replace(/\s+/g, '_')}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading certificate:', err);
            const errorMessage = err.response?.data?.message || 'Certificate download failed. Please contact support.';
            alert(errorMessage);
        }
    };

    const getStatusBadge = (status) => {
        if (status === 'certified') {
            return (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                    <Award className="w-3.5 h-3.5" />
                    Certified
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                <CheckCircle className="w-3.5 h-3.5" />
                Completed
            </span>
        );
    };

    // Extract unique categories and years (filter out NULL dates)
    const categories = ['all', ...new Set(records.map(r => r.category))];
    const years = ['all', ...new Set(
        records
            .filter(r => r.completion_date) // Filter out NULL dates
            .map(r => new Date(r.completion_date).getFullYear().toString())
    )].sort().reverse();

    // Calculate statistics
    const totalCompleted = records.length;
    const totalCertified = records.filter(r => r.status === 'certified').length;
    const averageScore = records.length > 0
        ? Math.round(records.reduce((sum, r) => sum + (r.score || 0), 0) / records.length)
        : 0;

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200">
            {/* Session Timeout Manager */}
            <SessionManager />

            {/* Header */}
            <header className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white shadow-lg sticky top-0 z-50 print:hidden">
                <div className="max-w-7xl mx-auto px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate('/training-portal')}
                                className="p-2 hover:bg-white/20 rounded-lg transition-all"
                                title="Back to Training Portal"
                            >
                                <ArrowLeft className="w-6 h-6" />
                            </button>
                            <div className="flex items-center gap-3">
                                <Award className="w-8 h-8" />
                                <div>
                                    <h1 className="text-2xl font-bold">My Training Records</h1>
                                    <p className="text-sm opacity-90">Complete Training History & Certificates</p>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={handlePrintTranscript}
                            className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all"
                        >
                            <Printer className="w-5 h-5" />
                            <span className="font-semibold">Print Transcript</span>
                        </button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-6 py-8">
                {/* Statistics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 print:mb-4">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-blue-100 rounded-lg">
                                <CheckCircle className="w-8 h-8 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-gray-600 text-sm font-semibold">Total Completed</p>
                                <p className="text-3xl font-bold text-gray-900">{totalCompleted}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-green-100 rounded-lg">
                                <Award className="w-8 h-8 text-green-600" />
                            </div>
                            <div>
                                <p className="text-gray-600 text-sm font-semibold">Certified</p>
                                <p className="text-3xl font-bold text-gray-900">{totalCertified}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-purple-100 rounded-lg">
                                <TrendingUp className="w-8 h-8 text-purple-600" />
                            </div>
                            <div>
                                <p className="text-gray-600 text-sm font-semibold">Average Score</p>
                                <p className="text-3xl font-bold text-gray-900">{averageScore}%</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8 print:hidden">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search training records..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E91E63]/50 focus:border-[#E91E63]"
                            />
                        </div>

                        {/* Category Filter */}
                        <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E91E63]/50 focus:border-[#E91E63]"
                        >
                            {categories.map(cat => (
                                <option key={cat} value={cat}>
                                    {cat === 'all' ? 'All Categories' : cat}
                                </option>
                            ))}
                        </select>

                        {/* Year Filter */}
                        <select
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(e.target.value)}
                            className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E91E63]/50 focus:border-[#E91E63]"
                        >
                            {years.map(year => (
                                <option key={year} value={year}>
                                    {year === 'all' ? 'All Years' : year}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Loading State */}
                {loading && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
                        <Loader2 className="w-12 h-12 text-[#E91E63] mx-auto mb-4 animate-spin" />
                        <p className="text-gray-600 text-lg">Loading training records...</p>
                    </div>
                )}

                {/* Error State */}
                {error && (
                    <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8 text-center">
                        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                        <p className="text-red-700 font-semibold mb-4">{error}</p>
                        <button
                            onClick={fetchTrainingRecords}
                            className="px-6 py-2 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-all"
                        >
                            Retry
                        </button>
                    </div>
                )}

                {/* Training Records Table */}
                {!loading && !error && filteredRecords.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Training Module
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Category
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Completion Date
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Score
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                            Blockchain
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider print:hidden">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {filteredRecords.map((record) => (
                                        <tr key={record.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4">
                                                <div>
                                                    <p className="font-semibold text-gray-900">{record.module_name}</p>
                                                    <p className="text-sm text-gray-500 font-mono">{record.module_code}</p>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                                                    {record.category}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2 text-gray-700">
                                                    <Calendar className="w-4 h-4 text-gray-400" />
                                                    <span className="text-sm">{formatDate(record.completion_date)}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                {record.score ? (
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[80px]">
                                                            <div
                                                                className={`h-2 rounded-full ${
                                                                    record.score >= 80 ? 'bg-green-500' :
                                                                    record.score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                                                                }`}
                                                                style={{ width: `${record.score}%` }}
                                                            />
                                                        </div>
                                                        <span className="font-semibold text-gray-900">{record.score}%</span>
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400 text-sm">N/A</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                {getStatusBadge(record.status)}
                                            </td>
                                            <td className="px-6 py-4">
                                                {record.blockchain_hash ? (
                                                    <div className="flex flex-col gap-1">
                                                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-semibold">
                                                            <CheckCircle className="w-3 h-3" />
                                                            Verified
                                                        </span>
                                                        <span className="text-xs text-gray-500 font-mono" title={record.blockchain_hash}>
                                                            {record.blockchain_hash.substring(0, 8)}...
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-500 rounded text-xs">
                                                        <Clock className="w-3 h-3" />
                                                        Pending
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 print:hidden">
                                                <div className="flex items-center gap-2">
                                                    {record.status === 'certified' && record.certificate_path && (
                                                        <button
                                                            onClick={() => handleDownloadCertificate(record)}
                                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-700 hover:bg-green-200 rounded-lg text-sm font-semibold transition-all"
                                                            title="Download Certificate"
                                                        >
                                                            <Download className="w-4 h-4" />
                                                            Certificate
                                                        </button>
                                                    )}
                                                    {record.signature_id && (
                                                        <button
                                                            onClick={() => navigate(`/verify/certificate/${record.signature_id}`)}
                                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg text-sm font-semibold transition-all"
                                                            title="Verify Certificate"
                                                        >
                                                            <Award className="w-4 h-4" />
                                                            Verify
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => navigate(`/training/module/${record.module_id}`)}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg text-sm font-semibold transition-all"
                                                        title="View Details"
                                                    >
                                                        <ExternalLink className="w-4 h-4" />
                                                        Details
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* No Records */}
                {!loading && !error && filteredRecords.length === 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
                        <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-2xl font-bold text-gray-700 mb-2">No Training Records Found</h3>
                        <p className="text-gray-600 mb-6">
                            {searchTerm || selectedCategory !== 'all' || selectedYear !== 'all'
                                ? 'Try adjusting your filters.'
                                : 'Complete your first training module to see records here.'}
                        </p>
                        <button
                            onClick={() => navigate('/training-portal')}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#475569] to-[#334155] text-white font-semibold rounded-lg hover:from-[#334155] hover:to-[#1e293b] transition-all"
                        >
                            <ArrowLeft className="w-5 h-5" />
                            Browse Training Modules
                        </button>
                    </div>
                )}

                {/* Print Footer - Only visible when printing */}
                <div className="hidden print:block mt-8 pt-4 border-t border-gray-300">
                    <div className="text-center text-sm text-gray-600">
                        <p className="font-semibold mb-1">Training Transcript for {user?.first_name} {user?.last_name}</p>
                        <p>Email: {user?.email}</p>
                        <p>Printed: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                        <p className="mt-2 text-xs">This document is an official training record from KLINiSOL CRO Training Management System</p>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white py-6 px-6 mt-12 print:hidden">
                <div className="max-w-7xl mx-auto text-center">
                    <h3 className="text-xl font-bold mb-2">KLINiSOL</h3>
                    <p className="text-sm opacity-90">&copy; 2025 KLINiSOL CRO. All rights reserved.</p>
                </div>
            </footer>
        </div>
    );
};

export default TrainingRecords;
